import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Check, ImageOff, Loader2, Plus, Search, Send, Trash2 } from 'lucide-react';
import { agentService } from '@/services/agentService';
import { customerService } from '@/services/customerService';
import { productService } from '@/services/productService';
import {
  quoteService,
  type PhotoQuote,
  type PhotoQuoteRecipientType,
  type PhotoQuoteStatus,
} from '@/services/quoteService';
import type { Agent, Customer, Product } from '@/types/domain';
import PageHeader from '@/components/shared/PageHeader';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ProductImagesDialog, ProductImagesButton } from './ProductImagesDialog';
import { QuoteRecipientFields, agentCanReceive } from './QuoteRecipientFields';
import { cn } from '@/lib/utils';

interface DraftLine {
  zoho_item_id: string;
  sku: string;
  name: string;
  brand_name: string | null;
  image_url: string | null;
  pack_qty: number;
  /** Held price for the customer — starts at the product rate, editable here. */
  unit_price: number;
  quantity: number;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
}

function lineFromProduct(product: Product): DraftLine {
  const packQty = product.pack_qty && product.pack_qty > 0 ? product.pack_qty : 1;
  return {
    zoho_item_id: product.zoho_item_id,
    sku: product.sku,
    name: product.name,
    brand_name: product.brand_name_cleaned || product.brand || null,
    image_url: product.image_url,
    pack_qty: packQty,
    unit_price: Number(product.rate) || 0,
    quantity: packQty,
  };
}

/**
 * Build a Photo Quote: pick the recipient (a customer, or a sales agent with an optional
 * customer), pick products from any brand, set the quantities and the held prices, then send it
 * to the recipient's Trade Portal account.
 */
export default function QuoteBuilder() {
  const navigate = useNavigate();
  const { quoteId } = useParams<{ quoteId: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(quoteId);
  usePageTitle(isEdit ? 'Edit Photo Quote' : 'New Photo Quote');

  // Recipient: a customer, or an agent (optionally for a customer)
  const [recipientType, setRecipientType] = useState<PhotoQuoteRecipientType>('customer');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentId, setAgentId] = useState('');
  const [customer, setCustomer] = useState<Customer | null>(null);
  /** Status of the quote being edited; the recipient can change only while it is a draft. */
  const [quoteStatus, setQuoteStatus] = useState<PhotoQuoteStatus | null>(null);
  const recipientLocked = isEdit && quoteStatus !== 'draft';
  const agent = agents.find((row) => row.id === agentId) ?? null;

  // Product browse
  const [brands, setBrands] = useState<{ brand: string; count: number }[]>([]);
  const [brand, setBrand] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  /** The product whose image manager is open. */
  const [imagesFor, setImagesFor] = useState<Product | null>(null);

  // Quote
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lineIds = useMemo(() => new Set(lines.map((l) => l.zoho_item_id)), [lines]);
  const total = useMemo(
    () => lines.reduce((sum, line) => sum + line.unit_price * line.quantity, 0),
    [lines],
  );

  // Preselect the customer when arriving from CustomerDetail.
  useEffect(() => {
    const preset = searchParams.get('customer');
    if (!preset || customer) return;
    customerService
      .getById(preset)
      .then(setCustomer)
      .catch(() => undefined);
  }, [searchParams, customer]);

  // Load an existing draft for editing.
  useEffect(() => {
    if (!quoteId) return;
    quoteService
      .getById(quoteId)
      .then((quote: PhotoQuote) => {
        setQuoteStatus(quote.status);
        setRecipientType(quote.recipient_type ?? 'customer');
        setAgentId(quote.recipient_agent_id ?? '');
        setTitle(quote.title ?? '');
        setMessage(quote.message ?? '');
        setValidUntil(quote.valid_until ? quote.valid_until.slice(0, 10) : '');
        setLines(
          quote.items
            .filter((item) => !item.removed)
            .map((item) => ({
              zoho_item_id: item.zoho_item_id,
              sku: item.sku,
              name: item.name,
              brand_name: item.brand_name,
              image_url: item.image_url,
              pack_qty: item.pack_qty || 1,
              unit_price: Number(item.unit_price),
              quantity: item.customer_quantity ?? item.quantity,
            })),
        );
        return quote.customer_id ? customerService.getById(quote.customer_id) : null;
      })
      .then((found) => found && setCustomer(found))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load quote'));
  }, [quoteId]);

  useEffect(() => {
    productService
      .getBrands()
      .then(setBrands)
      .catch(() => undefined);
    // 'trade-portal' is the system agent orders from the portal are filed under, not a person.
    agentService
      .list()
      .then((rows) => setAgents(rows.filter((row) => row.id !== 'trade-portal')))
      .catch(() => undefined);
  }, []);

  const loadProducts = useCallback(async () => {
    if (!brand && !productSearch.trim()) {
      setProducts([]);
      return;
    }
    setProductsLoading(true);
    try {
      const result = await productService.list({
        brand: brand || undefined,
        search: productSearch.trim() || undefined,
        status: 'active',
        limit: 60,
        sort_by: 'name',
      });
      setProducts(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setProductsLoading(false);
    }
  }, [brand, productSearch]);

  useEffect(() => {
    const timer = setTimeout(loadProducts, productSearch ? 300 : 0);
    return () => clearTimeout(timer);
  }, [loadProducts, productSearch]);

  function addProduct(product: Product) {
    setLines((current) =>
      current.some((l) => l.zoho_item_id === product.zoho_item_id)
        ? current
        : [...current, lineFromProduct(product)],
    );
  }

  function updateLine(zohoItemId: string, patch: Partial<DraftLine>) {
    setLines((current) =>
      current.map((line) => (line.zoho_item_id === zohoItemId ? { ...line, ...patch } : line)),
    );
  }

  /** Quantities snap up to a whole number of packs, the same rule the ordering screens use. */
  function snapQuantity(line: DraftLine, value: number): number {
    const pack = Math.max(line.pack_qty, 1);
    if (!Number.isFinite(value) || value <= 0) return pack;
    return Math.ceil(value / pack) * pack;
  }

  async function persist(): Promise<PhotoQuote | null> {
    if (recipientType === 'customer' && !customer) {
      setError('Choose a customer first');
      return null;
    }
    if (recipientType === 'agent' && (!agent || !agentCanReceive(agent)) && !recipientLocked) {
      setError('Choose an agent with Trade Portal access');
      return null;
    }
    if (lines.length === 0) {
      setError('Add at least one product');
      return null;
    }
    setError(null);

    const payload = {
      title: title.trim() || null,
      message: message.trim() || null,
      valid_until: validUntil || null,
      items: lines.map((line) => ({
        zoho_item_id: line.zoho_item_id,
        quantity: line.quantity,
        unit_price: line.unit_price,
      })),
    };
    // The backend takes all three recipient fields together, and only on a draft.
    const recipient = {
      recipient_type: recipientType,
      recipient_agent_id: recipientType === 'agent' ? agentId : null,
      customer_id: customer?.id ?? null,
    };

    if (quoteId) {
      return quoteService.update(quoteId, recipientLocked ? payload : { ...recipient, ...payload });
    }
    return quoteService.create({ ...recipient, ...payload });
  }

  async function handleSaveDraft() {
    setSaving(true);
    try {
      const quote = await persist();
      if (quote) navigate(`/quotes/${quote.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save quote');
    } finally {
      setSaving(false);
    }
  }

  async function handleSend() {
    setSaving(true);
    try {
      const quote = await persist();
      if (!quote) return;
      const result = await quoteService.send(quote.id);
      if (!result.email.success) {
        setError(`Quote saved and visible in the portal, but the email failed: ${result.email.error}`);
      }
      navigate(`/quotes/${quote.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send quote');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={isEdit ? 'Edit Photo Quote' : 'New Photo Quote'}
        subtitle={
          recipientType === 'agent'
            ? agent
              ? `Agent: ${agent.name}${customer ? ` · for ${customer.company_name}` : ''}`
              : 'Choose an agent to start'
            : customer
              ? customer.company_name
              : 'Choose a customer to start'
        }
        breadcrumbs={[{ label: 'Photo Quotes', href: '/quotes' }, { label: isEdit ? 'Edit' : 'New' }]}
      />

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        {/* Product browser */}
        <div className="space-y-4">
          <div className="space-y-3 rounded-lg border p-4">
            <QuoteRecipientFields
              recipientType={recipientType}
              onRecipientTypeChange={setRecipientType}
              agents={agents}
              agentId={agentId}
              onAgentChange={setAgentId}
              customer={customer}
              onCustomerChange={setCustomer}
              disabled={recipientLocked}
            >
              <div className="space-y-1.5">
                <Label htmlFor="quote-brand">Brand</Label>
                <select
                  id="quote-brand"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs"
                >
                  <option value="">All brands</option>
                  {brands.map((row) => (
                    <option key={row.brand} value={row.brand}>
                      {row.brand} ({row.count})
                    </option>
                  ))}
                </select>
              </div>
            </QuoteRecipientFields>

            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Search products by name or SKU"
                className="pl-8"
              />
            </div>
          </div>

          {productsLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {!productsLoading && products.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Pick a brand or search to find products.
            </p>
          )}

          {products.length > 0 && (
            <div className="overflow-hidden rounded-lg border">
              {/* Column headers, hidden on narrow screens where the rows stack. */}
              <div className="hidden items-center gap-3 border-b bg-muted/40 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground sm:flex">
                <span className="w-10" />
                <span className="min-w-0 flex-1">Product</span>
                <span className="w-28 shrink-0">Brand</span>
                <span className="w-14 shrink-0 text-right">Pack</span>
                <span className="w-16 shrink-0 text-right">Stock</span>
                <span className="w-20 shrink-0 text-right">Price</span>
                <span className="w-24 shrink-0" />
              </div>

              <ul className="divide-y">
                {products.map((product) => {
                  const added = lineIds.has(product.zoho_item_id);
                  return (
                    <li
                      key={product.zoho_item_id}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2 text-sm',
                        added && 'bg-primary/5',
                      )}
                    >
                      <div className="size-10 shrink-0 overflow-hidden rounded bg-muted">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt=""
                            loading="lazy"
                            className="size-full object-cover"
                          />
                        ) : (
                          <ImageOff className="m-auto mt-2.5 size-4 text-muted-foreground" />
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium sm:text-sm">{product.name}</p>
                        <p className="truncate text-[11px] text-muted-foreground">
                          {product.sku}
                          <span className="sm:hidden">
                            {' · '}
                            {formatCurrency(Number(product.rate))} · pack {product.pack_qty || 1}
                          </span>
                        </p>
                      </div>

                      <span className="hidden w-28 shrink-0 truncate text-xs text-muted-foreground sm:block">
                        {product.brand_name_cleaned || product.brand}
                      </span>
                      <span className="hidden w-14 shrink-0 text-right text-xs tabular-nums sm:block">
                        {product.pack_qty || 1}
                      </span>
                      <span
                        className={cn(
                          'hidden w-16 shrink-0 text-right text-xs tabular-nums sm:block',
                          product.stock_on_hand > 0 ? 'text-muted-foreground' : 'text-destructive',
                        )}
                      >
                        {product.stock_on_hand}
                      </span>
                      <span className="hidden w-20 shrink-0 text-right text-xs tabular-nums sm:block">
                        {formatCurrency(Number(product.rate))}
                      </span>

                      <div className="flex w-24 shrink-0 items-center justify-end gap-1">
                        <ProductImagesButton
                          hasImage={Boolean(product.image_url)}
                          onPress={() => setImagesFor(product)}
                        />
                        <Button
                          intent={added ? 'outline' : 'primary'}
                          size="xs"
                          onPress={() => addProduct(product)}
                          isDisabled={added}
                        >
                          {added ? <Check className="size-3.5" /> : <Plus className="size-3.5" />}
                          {added ? 'Added' : 'Add'}
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        {/* Quote panel */}
        <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          <div className="space-y-3 rounded-lg border p-4">
            <div className="space-y-1.5">
              <Label htmlFor="quote-title">Title</Label>
              <Input
                id="quote-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Autumn picks for you"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="quote-message">
                {recipientType === 'agent' ? 'Note to agent' : 'Note to customer'}
              </Label>
              <textarea
                id="quote-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="A few pieces I think would work well for you…"
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="quote-valid">Prices held until</Label>
              <Input
                id="quote-valid"
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            </div>
          </div>

          <div className="rounded-lg border">
            <div className="flex items-center justify-between border-b px-4 py-2.5 text-sm font-medium">
              <span>Selection</span>
              <span className="text-muted-foreground">{lines.length}</span>
            </div>
            <ul className="max-h-[420px] divide-y overflow-auto">
              {lines.length === 0 && (
                <li className="px-4 py-6 text-center text-sm text-muted-foreground">
                  No products yet — click a product to add it.
                </li>
              )}
              {lines.map((line) => (
                <li key={line.zoho_item_id} className="flex gap-3 p-3">
                  <div className="size-14 shrink-0 overflow-hidden rounded bg-muted">
                    {line.image_url && (
                      <img src={line.image_url} alt="" className="size-full object-cover" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="line-clamp-2 text-xs font-medium">{line.name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {line.sku} · pack {line.pack_qty}
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={line.pack_qty}
                        step={line.pack_qty}
                        value={line.quantity}
                        onChange={(e) =>
                          updateLine(line.zoho_item_id, { quantity: Number(e.target.value) })
                        }
                        onBlur={(e) =>
                          updateLine(line.zoho_item_id, {
                            quantity: snapQuantity(line, Number(e.target.value)),
                          })
                        }
                        className="h-8 w-20"
                        aria-label={`Quantity for ${line.sku}`}
                      />
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={line.unit_price}
                        onChange={(e) =>
                          updateLine(line.zoho_item_id, { unit_price: Number(e.target.value) })
                        }
                        className="h-8 w-24"
                        aria-label={`Price for ${line.sku}`}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setLines((current) =>
                            current.filter((l) => l.zoho_item_id !== line.zoho_item_id),
                          )
                        }
                        className="ml-auto text-muted-foreground hover:text-destructive"
                        aria-label={`Remove ${line.sku}`}
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between border-t px-4 py-3 text-sm font-medium">
              <span>Total</span>
              <span className="tabular-nums">{formatCurrency(total)}</span>
            </div>
          </div>

          <div className="flex gap-2">
            <Button intent="outline" className="flex-1" onPress={handleSaveDraft} isDisabled={saving}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : null}
              Save draft
            </Button>
            <Button className="flex-1" onPress={handleSend} isDisabled={saving}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
              {recipientType === 'agent' ? 'Send to agent' : 'Send to customer'}
            </Button>
          </div>
        </aside>
      </div>

      {imagesFor && (
        <ProductImagesDialog
          product={imagesFor}
          onClose={() => setImagesFor(null)}
          // A new image can become the product's main one, so refresh the browse rows.
          onUploaded={() => void loadProducts()}
        />
      )}
    </div>
  );
}
